/**
 * LOP CHINH SUA - hien ket qua cua cac lenh text.replace dang co hieu luc.
 *
 * VI SAO LOP NAY TON TAI THAY VI VE LAI CANVAS
 * --------------------------------------------
 * Canvas duoc pdf.js ve tu TAI LIEU GOC, va tai lieu goc la BAT BIEN (ADR-003):
 * cac lenh chi duoc vat chat hoa thanh byte khi bam Luu. Neu muon canvas hien
 * duoc lan sua, ta phai xuat ca tep roi mo lai sau MOI phim go - vai giay cho
 * mot tai lieu vai megabyte, va ma khoi se doi sau moi lan mo lai.
 *
 * Vi vay lop nay ve DUNG NHUNG GI DUONG GHI SE GHI RA, khong hon:
 *   1. mot hinh chu nhat mau nen che len DON VI DA CHON  (page.drawRectangle)
 *   2. van ban moi dat tren do                            (showText)
 * Cung hop bao, cung co chu da qua bo cuc, cung mau chu, cung font Noto Sans
 * dong goi san. Day khong phai mot ban dung dep hon ket qua that - no la mo
 * phong cua chinh hai buoc do.
 *
 * DIEM PHAI GIU DUNG: hop bao o day la hop bao cua PHAM VI (run / dong / khoi),
 * lay tu chinh resolveScope() ma duong ghi dung. Lay nham hop bao cua khoi cha
 * thi ban xem truoc se ve mot mang che phu ca bang trong khi tep xuat ra chi che
 * mot o - hai ben noi hai chuyen khac nhau, va nguoi dung tin ban xem truoc.
 *
 * SU KHAC BIET CON LAI, va ta khong giau: cach ngat dong cua trinh duyet khong
 * dam bao trung tuyet doi voi layoutInBox(). So dong va co chu thi trung, vi ca
 * hai deu lay tu bao cao chay thu that cua loi.
 */
import type { PdfRect, TextBlock } from '@pdfeditor/types';
import { pdfRectToCss, type PageGeometry } from './geometry';
import { parseScopeId, resolveScope, type EditScope } from '../core/edit/scope';
import type { TextAlign } from '../core/edit/layout';
import { cssColor, runsColorRgb, runsFontSizePt } from '../ui/textEditCore';
import { getFontFamily } from '../core/fonts/families';
import type { EditedItem } from './editedItems';

export type { EditedItem } from './editedItems';
export { editedItemsFromCommands } from './editedItems';

/** So do dat chu lay tu bao cao chay thu cua loi - khong phai do giao dien doan. */
export interface EditRenderHint {
  fontSizePt: number;
  lineCount: number;
  /**
   * Can le ma LOI da thuc su dung, chep thang tu TextReplaceReport.align.
   *
   * KHONG duoc goi inferAlign() o lop nay. Lop nay la mo phong cua duong ghi,
   * va mot phep suy luan chay hai lan tren hai tap dau vao hoi khac nhau (o day
   * la hop bao CSS da lam tron, ben kia la toa do PDF) se cho hai ket qua khac
   * nhau tren dung nhung truong hop sat nguong. Xem ghi chu cung noi dung o
   * coverRect.ts - lan truoc chinh la hop che di theo duong nay.
   */
  align: TextAlign;
  /**
   * Hop che do CHINH LOI tinh, toa do PDF.
   *
   * Bat buoc phai dung, khong duoc thay bang scope.bbox: khi chu cu co dau tieng
   * Viet chong tang, hop che CAO HON bbox (dau vuon toi 1.048em, xem
   * coverRect.ts). Truoc day lop nay ve theo bbox nen ban xem truoc che hut va
   * de lai dau chu cu lo lung tren chu moi - trong khi tep xuat ra thi che dung.
   */
  coverRect: PdfRect | null;
}

export interface EditedTextLayerProps {
  blocks: readonly TextBlock[];
  /** maPhamVi -> lan sua dang co hieu luc (chu + kieu chu). Xem EditedItem. */
  edited: ReadonlyMap<string, EditedItem>;
  hints: ReadonlyMap<string, EditRenderHint>;
  geometry: PageGeometry;
}

/**
 * Mau hinh chu nhat che, phai TRUNG voi mac dinh cua applyTextReplace().
 * Doi mot ben ma quen ben kia thi ban xem truoc se noi doi ve ket qua that.
 */
const COVER_CSS = 'rgb(255, 255, 255)';

export function EditedTextLayer({ blocks, edited, hints, geometry }: EditedTextLayerProps) {
  if (edited.size === 0) return null;

  // Duyet theo CAC LENH chu khong theo cac khoi: mot khoi co the mang nhieu lan
  // sua o cac o khac nhau, va moi lan sua co hop bao rieng cua no.
  const items: Array<{ scope: EditScope; item: EditedItem }> = [];
  for (const [scopeId, item] of edited) {
    const { blockId } = parseScopeId(scopeId);
    const parent = blocks.find((b) => b.id === blockId);
    if (!parent) continue;
    const scope = resolveScope(parent, scopeId);
    if (!scope) continue;
    // KHONG bo qua "chu === chu goc" nua. Moi muc o day la mot lenh DANG CO HIEU
    // LUC (sau Ctrl+Z lenh khong con trong danh sach - xem EditedItem), nen ke ca
    // lan sua CHI doi phong / dam / nghieng / co chu ma GIU NGUYEN chu van phai ve
    // de len. Truoc day dieu kien nay bo qua chung, nen man hinh giu font cu trong
    // khi tep xuat ra da doi - dung phan nan cua nguoi dung ("preview vẫn font cũ,
    // bấm Lưu mới thấy đổi").
    items.push({ scope, item });
  }
  if (items.length === 0) return null;

  return (
    <div className="editedLayer" aria-hidden="true">
      {items.map(({ scope, item }) => {
        const hint = hints.get(scope.id);
        // Hop che cua LOI, khong phai bbox. Chi lui ve bbox khi loi chua kip tra
        // ve so do - luc do che hut mot chut van hon la khong ve gi.
        const rect = pdfRectToCss(hint?.coverRect ?? scope.bbox, geometry);
        const fontSizePx = (hint?.fontSizePt ?? fallbackSizePt(scope)) * geometry.zoom;
        // Buoc dong suy tu so dong THAT cua loi: chia deu chieu cao pham vi. Khi
        // chi co mot dong thi khong co gi de chia - dung 1.2 lan co chu.
        const lineCount = Math.max(1, hint?.lineCount ?? 1);
        const lineHeightPx =
          lineCount > 1 ? rect.height / lineCount : Math.max(fontSizePx * 1.2, rect.height);

        return (
          <div
            key={scope.id}
            className="editedBlock"
            style={{
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`,
              background: COVER_CSS,
              // Can le cua LOI. Khong co so do thi lui ve 'left' - dung mac dinh
              // cua trinh duyet, va la thu duy nhat ta biet chac khi chua do.
              textAlign: hint?.align ?? 'left',
            }}
          >
            <span
              className="editedBlockText"
              style={{
                // Ho font + dam / nghieng CUA LENH (nguon chan ly), khong phai mac
                // dinh cua CSS: phai la dung mat chu se duoc nhung, neu khong man
                // hinh ve mot dang con tep mang mot dang khac (bai hoc so 3).
                // cssStack luon dat ten @font-face dong goi len dau, nen khong bao
                // gio tut xuong font he thong. fontWeight / fontStyle chon dung mat
                // chu dam / nghieng cua ho font do trong styles.css.
                fontFamily: getFontFamily(item.fontFamily).cssStack,
                fontWeight: item.bold ? 700 : 400,
                fontStyle: item.italic ? 'italic' : 'normal',
                fontSize: `${fontSizePx}px`,
                lineHeight: `${lineHeightPx}px`,
                color: cssColor(runsColorRgb(scope.runs)),
              }}
            >
              {item.text}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function fallbackSizePt(scope: EditScope): number {
  const size = runsFontSizePt(scope.runs);
  return size > 0 ? size : Math.max(6, scope.bbox.height);
}
